import { ReactElement } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { AlignmentButton } from '@ui/actions'
import { Select } from '@ui/forms'
import { useElementLabel } from '@editorHooks'
import WallAlignmentConfirmDialog from '../../../../WallAlignmentConfirmDialog'
import {
  buildLowerStoreyOptions,
  buildUpperStoreyOptions,
  canSubmitConfiguredWallCut,
  NO_UPPER_STOREY,
} from '../utils'

export interface ConfiguredWallCutFormValues {
  lowerStorey: string
  upperStorey: string
}

interface Props {
  roofSlabGuids: string[]
  onRemoveRoofSlab: (guid: string) => void
  onStartSelecting: () => void
  onSelectAllRoofSlabs: () => void
  onDeselectAllRoofSlabs: () => void
  selectAllDisabled: boolean
  isSelectingRoofSlabs: boolean
  onSubmit: (values: ConfiguredWallCutFormValues) => Promise<void>
  isLoading: boolean
  storeyBoundaries: StoreyBoundaries
}

const ConfiguredWallCutFields = ({
  roofSlabGuids,
  onRemoveRoofSlab,
  onStartSelecting,
  onSelectAllRoofSlabs,
  onDeselectAllRoofSlabs,
  selectAllDisabled,
  isSelectingRoofSlabs,
  onSubmit,
  isLoading,
  storeyBoundaries,
}: Props): ReactElement => {
  const elementLabel = useElementLabel()
  const { handleSubmit } = useFormContext<ConfiguredWallCutFormValues>()
  const upperStorey = useWatch<ConfiguredWallCutFormValues, 'upperStorey'>({ name: 'upperStorey' })
  const canSubmit = canSubmitConfiguredWallCut(upperStorey ?? NO_UPPER_STOREY, roofSlabGuids.length)

  return (
    <Stack spacing={1} width="100%">
      <Stack direction="row" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Select<ConfiguredWallCutFormValues>
            name="lowerStorey"
            inputLabel="Unterkante"
            options={buildLowerStoreyOptions(storeyBoundaries)}
            data-cy="select-align-lower-storey"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Select<ConfiguredWallCutFormValues>
            name="upperStorey"
            inputLabel="Oberkante"
            options={buildUpperStoreyOptions(storeyBoundaries)}
            data-cy="select-align-upper-storey"
          />
        </Box>
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant="body2">Dachflächen</Typography>
        {roofSlabGuids.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: 1,
              rowGap: 0.5,
            }}
          >
            {roofSlabGuids.map(guid => (
              <Stack key={guid} direction="row" alignItems="center" spacing={0.5} minWidth={0}>
                <Typography noWrap variant="body2" sx={{ flex: 1 }}>
                  {elementLabel(guid)}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Dachfläche entfernen"
                  onClick={() => onRemoveRoofSlab(guid)}
                  sx={{ padding: 0 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Box>
        )}
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={onStartSelecting}
          data-cy="btn-select-align-roof-slabs"
        >
          Dachflächen anwählen
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={onDeselectAllRoofSlabs}
            disabled={roofSlabGuids.length === 0 && !isSelectingRoofSlabs}
            data-cy="btn-deselect-all-align-roof-slabs"
          >
            Alle abwählen
          </Button>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={onSelectAllRoofSlabs}
            disabled={selectAllDisabled}
            data-cy="btn-select-all-align-roof-slabs"
          >
            Alle auswählen
          </Button>
        </Stack>
      </Stack>
      {!canSubmit && (
        <Typography variant="caption" color="error">
          Mindestens eine Dachfläche auswählen.
        </Typography>
      )}
      <WallAlignmentConfirmDialog onConfirm={() => handleSubmit(onSubmit)()}>
        <AlignmentButton
          size="small"
          fullWidth
          loading={isLoading}
          disabled={!canSubmit}
          data-cy="btn-configured-align-wall"
        >
          zuschneiden
        </AlignmentButton>
      </WallAlignmentConfirmDialog>
    </Stack>
  )
}

export default ConfiguredWallCutFields
